/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import { FC, useMemo, useState } from 'react';
import { Box, Button, ButtonProps, Tooltip, useMediaQuery, useTheme } from '@mui/material';
import { useSelector } from 'react-redux';

import TestIds from '../../../../testIds';
import {
  selectAllFarmerAccounts,
  selectCurrentAccountNumber,
  selectIsCEUser,
} from '../../../azure-b2c/storage';
import AccountSwitcher from '../../../azure-b2c/components/AccountSwitcher';
import Modal from '../../../../components/Modal';
import { MarginStyledBox } from '../../../../styled/box';

import './SelectedAccount.scss';

export interface ISelectedAccountProps {
  enableAccountSwitcher?: boolean;
  fullScreenModal?: boolean;
  className?: string;
  buttonProps?: ButtonProps;
  truncateAccountName?: boolean;
}

const SelectedAccount: FC<ISelectedAccountProps> = ({
  enableAccountSwitcher,
  fullScreenModal,
  className,
  buttonProps,
  truncateAccountName,
}: ISelectedAccountProps): JSX.Element | undefined => {
  const theme = useTheme();
  const isMd = useMediaQuery(theme.breakpoints.up('md'));
  const selectedAccountNumber = useSelector(selectCurrentAccountNumber);
  const allAccounts = useSelector(selectAllFarmerAccounts);
  const selectedAccount = Object.values(allAccounts?.accounts ?? {}).find(
    (account) => account.accountNumber === selectedAccountNumber,
  );

  const farmlandsAccounts = useSelector(selectAllFarmerAccounts);
  const isCEUser = useSelector(selectIsCEUser);

  const [openModal, setOpenModal] = useState<boolean>(false);

  const canSwitchAccount = useMemo(
    (): boolean =>
      !!(enableAccountSwitcher && farmlandsAccounts?.accounts) &&
      !isCEUser &&
      Object.keys(farmlandsAccounts.accounts).length > 1,
    [enableAccountSwitcher, farmlandsAccounts?.accounts],
  );

  const accountName = useMemo((): JSX.Element | null => {
    if (!selectedAccount?.accountName) {
      return null;
    }

    const accountNameDisplay: JSX.Element = (
      <Box
        className='fl-selected-account__account-name'
        data-testid={TestIds.feat.account.userAccount.selectedAccount.accountName}
      >
        {selectedAccount?.accountName}
      </Box>
    );

    if (truncateAccountName && isMd) {
      return (
        <Tooltip
          title={selectedAccount?.accountName}
          className='fl-selected-account__account-name-tooltip'
          placement='top-end'
          enterDelay={1200}
          slotProps={{
            popper: {
              modifiers: [
                {
                  name: 'offset',
                  options: {
                    offset: [0, -12],
                  },
                },
              ],
            },
          }}
        >
          {accountNameDisplay}
        </Tooltip>
      );
    }

    return accountNameDisplay;
  }, [selectedAccount?.accountName, isMd, truncateAccountName]);

  if (selectedAccount) {
    const { onClick, ...switchAccountButtonProps } = buttonProps || {};
    return (
      <MarginStyledBox
        className={`fl-selected-account ${className || ''}`.trim()}
        data-testid={TestIds.feat.account.userAccount.selectedAccount.component}
      >
        {accountName}

        {canSwitchAccount && (
          <>
            <Button
              variant='farmlands2.0_default'
              color='primary'
              data-testid={TestIds.feat.account.userAccount.selectedAccount.switchAccount}
              onClick={(event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
                setOpenModal(true);
                onClick && onClick(event);
              }}
              {...switchAccountButtonProps}
            >
              Switch account
            </Button>

            {openModal && (
              <Modal
                title='Accounts'
                open
                handleClose={() => setOpenModal(false)}
                fullScreen={fullScreenModal}
                testId={TestIds.feat.account.userAccount.selectedAccount.switchAccountModal}
              >
                <AccountSwitcher closeAccountSwitcher={() => setOpenModal(false)} />
              </Modal>
            )}
          </>
        )}
      </MarginStyledBox>
    );
  }
};

export default SelectedAccount;
